// The member (player) session on the league site (registration plan step 2,
// docs/pages/member-accounts.md). Mounted by SiteTemplateHost around the
// public routes, not at App level: it uses router hooks and only the
// template-rendered site needs it.
//
// Fail closed (plan section 15): while the league is unknown or the
// `registration` add-on is off (tenantAccess.ts › addonEntitled, the strict
// path), the provider hands out MEMBER_AUTH_OFF itself, the same object as the
// default context value: no storage read, no request, no state change. That
// is what keeps a league without the add-on exactly as it was.
//
// When entitled:
//   no stored token          → signedOut (no request)
//   a stored token           → checking → GET /member/me
//     200                    → signedIn
//     401                    → memberApi ends the session → signedOut
//     403 / 404              → off for the rest of the session (a stale cached
//                              tenant still listing the add-on); token kept
//     503 / 429 / network    → unavailable; token kept; re-checked once on the
//                              next navigation and on the `online` event
// A generation counter drops a /me answer that an action has overtaken.
//
// My account (plan step 9) adds two actions:
//   changePassword  PUT /me/password; the server bumps token_version and
//                   answers a fresh token for this browser, which is stored
//                   here (the client never stores one) so this session goes
//                   on. When another tab stored a different token meanwhile,
//                   that one is kept and the session re-hydrates instead.
//                   When the session was ended on purpose meanwhile (Sign
//                   out here, a sign-out in another tab, markOff), the fresh
//                   token is dropped: a password change never undoes a
//                   sign-out. Only a clear by the 401 rule in THIS tab (a
//                   request sent with the old token, refused after the
//                   rotation) is rescued, with the profile held before the
//                   request. Another tab's 401-rule clear reaches this tab
//                   as a plain removal event, which cannot be told from a
//                   sign-out there, so that rare race ends signed out in
//                   every tab (fail closed; the member signs in again with
//                   the new password). A
//                   wrong current password is a 400 (never a sign-out); a 403
//                   / 404 marks off; a 401 never settles (memberApi ends it).
//   applyProfile    no request: a profile the page already got back (PUT /me,
//                   the photo upload) replaces the one held, so the header
//                   name updates; a /me answer still in flight is dropped.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useLeague } from './LeagueContext'
import { addonEntitled } from './tenantAccess'
import {
  MEMBER_TOKEN_KEY,
  MemberApiError,
  clearMemberToken,
  getMemberToken,
  isVerificationRequired,
  memberApi,
  onMemberSessionEnded,
  setMemberToken,
  type MemberAccount,
  type MemberProfile,
  type MemberRedeemReply,
  type MemberSession,
  type MemberSignUpInput,
  type MemberVerificationRequired,
  type MemberWaiverSign,
} from '@/services/memberApi'

export type MemberStatus = 'off' | 'checking' | 'signedOut' | 'signedIn' | 'unavailable'

export interface MemberAuthValue {
  status: MemberStatus
  account: MemberAccount | null
  profile: MemberProfile | null
  /**
   * Rejects with a MemberApiError. Resolves null once signed in, or with
   * the 202 body when the email must be verified first (October 29: a code
   * was emailed; no session yet).
   */
  signIn: (email: string, password: string) => Promise<MemberVerificationRequired | null>
  signUp: (input: MemberSignUpInput) => Promise<MemberVerificationRequired | null>
  /** The emailed code: on success the status is signedIn. Rejects with a MemberApiError. */
  verifyEmail: (email: string, code: string) => Promise<void>
  /** Send a new code (never rejects for an unknown address). */
  resendVerification: (email: string) => Promise<void>
  /**
   * Create an account and redeem an invitation in one step (registration
   * plan step 16). Stores the session exactly as signUp does and resolves
   * with the new entry and its registration card. Rejects with a
   * MemberApiError; a 404 invite_not_found is the invitation's, not the
   * add-on's, so it does not turn the session off.
   */
  signUpWithInvite: (input: SignUpWithInviteInput) => Promise<MemberRedeemReply | MemberVerificationRequired>
  /**
   * Create an account and join a team with its code in one step
   * (registration plan step 26). Stores the session exactly as signUp does
   * and resolves with the new entry and its registration card. Rejects with
   * a MemberApiError; a 404 team_code_not_found is the code's and a 429 the
   * miss limiter's, so neither turns the session off.
   */
  signUpWithTeamCode: (input: SignUpWithTeamCodeInput) => Promise<MemberRedeemReply | MemberVerificationRequired>
  /** Clears the member token only (the member API has no sign-out call). */
  signOut: () => void
  /** Re-check the stored session (GET /me). */
  refresh: () => void
  /** Hide every member entry point for the rest of the session. */
  markOff: () => void
  /** PUT /me/password; stores the fresh token. Rejects with a MemberApiError. */
  changePassword: (current: string, next: string) => Promise<void>
  /** Replace the held profile with one a write answered (no request). */
  applyProfile: (profile: MemberProfile) => void
}

export type SignUpWithInviteInput = MemberSignUpInput & {
  invite_token: string
  answers?: Record<string, string | number | boolean>
  phone?: string
  /** Step 34: the waiver signature, when the registration has one. */
  waiver?: MemberWaiverSign
}

export type SignUpWithTeamCodeInput = MemberSignUpInput & {
  team_code: string
  answers?: Record<string, string | number | boolean>
  phone?: string
  /** Step 34: the waiver signature, when the registration has one. */
  waiver?: MemberWaiverSign
}

const noop = () => {}
const noopAsync = async () => {}
const offReject = async (): Promise<never> => {
  throw new MemberApiError(403, 'feature_not_entitled', '', null, null)
}

/** The value outside a provider and whenever the add-on is off. One frozen object. */
export const MEMBER_AUTH_OFF: MemberAuthValue = Object.freeze({
  status: 'off',
  account: null,
  profile: null,
  signIn: async () => null,
  signUp: async () => null,
  verifyEmail: noopAsync,
  resendVerification: noopAsync,
  signUpWithInvite: offReject,
  signUpWithTeamCode: offReject,
  signOut: noop,
  refresh: noop,
  markOff: noop,
  changePassword: noopAsync,
  applyProfile: noop,
}) as MemberAuthValue

const MemberAuthContext = createContext<MemberAuthValue>(MEMBER_AUTH_OFF)

/** The member session; 'off' outside a provider (never throws). */
export function useMemberAuth(): MemberAuthValue {
  return useContext(MemberAuthContext)
}

interface MemberState {
  /** The league the state belongs to (`String(league.id)`); NONE's never matches one. */
  key: string
  status: Exclude<MemberStatus, 'off'> | 'off'
  account: MemberAccount | null
  profile: MemberProfile | null
}

const NONE: MemberState = { key: '\u0000none', status: 'signedOut', account: null, profile: null }

function seed(key: string, off: boolean): MemberState {
  if (off) return { key, status: 'off', account: null, profile: null }
  return { key, status: getMemberToken() ? 'checking' : 'signedOut', account: null, profile: null }
}

export function MemberAuthProvider({ children }: { children: ReactNode }) {
  const { league } = useLeague()
  const entitled = addonEntitled(league, 'registration')
  // Null while the add-on is off: nothing below reads storage or fetches.
  const hydKey = entitled ? String(league?.id ?? '') : null
  const { pathname } = useLocation()

  const offRef = useRef(false)
  const genRef = useRef(0)
  /**
   * Bumped by every deliberate end of the session: signOut, markOff and a
   * removal event from another tab (a sign-out there, or its 401 rule: the
   * two look alike). Never by this tab's own 401 rule.
   */
  const endedRef = useRef(0)
  /** The generation of the /me check in flight (0: none). */
  const meGenRef = useRef(0)
  const [state, setState] = useState<MemberState>(() => (hydKey !== null ? seed(hydKey, false) : NONE))
  // Until the hydration effect commits for this league, derive the status
  // (no 'checking' flash for a signed-out fan, no stale member of another league).
  const current = hydKey !== null && state.key !== hydKey ? seed(hydKey, offRef.current) : state
  const statusRef = useRef(current.status)
  statusRef.current = current.status
  const keyRef = useRef(hydKey)
  keyRef.current = hydKey
  const currentRef = useRef(current)
  currentRef.current = current

  const markOff = useCallback(() => {
    offRef.current = true
    endedRef.current++
    genRef.current++
    setState((s) => ({ key: keyRef.current ?? s.key, status: 'off', account: null, profile: null }))
  }, [])

  const hydrate = useCallback(() => {
    const key = keyRef.current
    if (key === null || offRef.current) return
    const gen = ++genRef.current
    if (!getMemberToken()) {
      setState({ key, status: 'signedOut', account: null, profile: null })
      return
    }
    setState((s) => ({ key, status: 'checking', account: s.key === key ? s.account : null, profile: s.key === key ? s.profile : null }))
    meGenRef.current = gen
    memberApi.me().then(
      (res) => {
        if (meGenRef.current === gen) meGenRef.current = 0
        if (gen !== genRef.current) return
        setState({ key, status: 'signedIn', account: res.account, profile: res.profile })
      },
      (err) => {
        if (meGenRef.current === gen) meGenRef.current = 0
        if (gen !== genRef.current) return
        if (err instanceof MemberApiError && (err.status === 403 || err.status === 404)) {
          markOff()
          return
        }
        setState((s) => ({ ...s, key, status: 'unavailable' }))
      },
    )
  }, [markOff])

  // Hydrate once per league while entitled.
  useEffect(() => {
    if (hydKey === null) return
    hydrate()
    return () => {
      genRef.current++
    }
  }, [hydKey, hydrate])

  // A member 401 anywhere ended the session (memberApi already cleared the token).
  useEffect(() => {
    if (hydKey === null) return
    return onMemberSessionEnded(() => {
      genRef.current++
      setState((s) => ({ key: s.key, status: offRef.current ? 'off' : 'signedOut', account: null, profile: null }))
    })
  }, [hydKey])

  // Another tab signed in or out.
  useEffect(() => {
    if (hydKey === null) return
    const onStorage = (e: StorageEvent) => {
      if (e.key !== MEMBER_TOKEN_KEY && e.key !== null) return
      if (offRef.current) return
      if (e.key === null ? !getMemberToken() : e.newValue === null) {
        // A removal while a token is stored again (a sign-in or a password
        // change racing it in another tab): check that token, don't sign out.
        if (getMemberToken() !== null) {
          hydrate()
          return
        }
        // A sign-out there, or there the 401 rule: indistinguishable here, so
        // both count as a deliberate end (a password change in flight here
        // drops its fresh token).
        endedRef.current++
        genRef.current++
        setState({ key: hydKey, status: 'signedOut', account: null, profile: null })
      } else if (e.key !== null && e.newValue !== e.oldValue) {
        hydrate()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [hydKey, hydrate])

  // While unavailable: one re-check on the next navigation, and when back online.
  const lastPath = useRef(pathname)
  useEffect(() => {
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    if (statusRef.current === 'unavailable') hydrate()
  }, [pathname, hydrate])
  const unavailable = current.status === 'unavailable'
  useEffect(() => {
    if (!unavailable) return
    const onOnline = () => hydrate()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [unavailable, hydrate])

  const signIn = useCallback(async (email: string, password: string) => {
    genRef.current++
    try {
      const res = await memberApi.signIn(email, password)
      if (isVerificationRequired(res)) return res
      genRef.current++
      setMemberToken(res.token)
      setState({ key: keyRef.current ?? '', status: 'signedIn', account: res.account, profile: res.profile })
      return null
    } catch (err) {
      if (err instanceof MemberApiError && (err.status === 403 || err.status === 404)) markOff()
      throw err
    }
  }, [markOff])

  const signUp = useCallback(async (input: MemberSignUpInput) => {
    genRef.current++
    try {
      const res = await memberApi.signUp(input)
      if (isVerificationRequired(res)) return res
      genRef.current++
      setMemberToken(res.token)
      setState({ key: keyRef.current ?? '', status: 'signedIn', account: res.account, profile: res.profile })
      return null
    } catch (err) {
      if (err instanceof MemberApiError && (err.status === 403 || err.status === 404)) markOff()
      throw err
    }
  }, [markOff])

  const verifyEmail = useCallback(async (email: string, code: string) => {
    genRef.current++
    try {
      const res = await memberApi.verifyEmail(email, code)
      genRef.current++
      setMemberToken(res.token)
      setState({ key: keyRef.current ?? '', status: 'signedIn', account: res.account, profile: res.profile })
    } catch (err) {
      if (err instanceof MemberApiError && (err.status === 403 || err.status === 404)) markOff()
      throw err
    }
  }, [markOff])

  const resendVerification = useCallback(async (email: string) => {
    await memberApi.resendVerification(email)
  }, [])

  /**
   * A sign-up that also redeems (an invitation or a team code): store the
   * session as signUp does and hand back the entry. `ownNotFound` is the 404
   * code that belongs to the invitation or the code, not to the add-on; any
   * other 403 / 404 means the member API is off for this site (quirk 20). A
   * 429 never turns it off.
   */
  const signUpRedeeming = useCallback(
    async (
      send: () => Promise<(MemberSession & MemberRedeemReply) | MemberVerificationRequired>,
      ownNotFound: string,
    ): Promise<MemberRedeemReply | MemberVerificationRequired> => {
      genRef.current++
      try {
        const res = await send()
        // October 29: the account exists but its email needs the code first.
        if (isVerificationRequired(res)) return res
        genRef.current++
        setMemberToken(res.token)
        setState({ key: keyRef.current ?? '', status: 'signedIn', account: res.account, profile: res.profile })
        return { entry: res.entry, registration: res.registration }
      } catch (err) {
        if (err instanceof MemberApiError && (err.status === 403 || (err.status === 404 && err.code !== ownNotFound))) markOff()
        throw err
      }
    },
    [markOff],
  )

  const signUpWithInvite = useCallback(
    (input: SignUpWithInviteInput) => signUpRedeeming(() => memberApi.signUpWithInvite(input), 'invite_not_found'),
    [signUpRedeeming],
  )

  const signUpWithTeamCode = useCallback(
    (input: SignUpWithTeamCodeInput) => signUpRedeeming(() => memberApi.signUpWithTeamCode(input), 'team_code_not_found'),
    [signUpRedeeming],
  )

  const changePassword = useCallback(async (current: string, next: string) => {
    const before = getMemberToken()
    const ended = endedRef.current
    const held = currentRef.current.profile
    genRef.current++
    try {
      const res = await memberApi.changePassword(current, next)
      // Signed out (here or in another tab) or marked off meanwhile: that wins.
      if (endedRef.current !== ended || offRef.current) return
      genRef.current++
      const now = getMemberToken()
      if (now === before || now === null) {
        // null: the 401 rule cleared the old token (a request refused after
        // the rotation), which also dropped the profile; restore both.
        setMemberToken(res.token)
        setState((s) => ({ key: keyRef.current ?? s.key, status: 'signedIn', account: res.account, profile: s.profile ?? held }))
      } else {
        // Another tab signed someone in meanwhile: its token wins.
        hydrate()
      }
    } catch (err) {
      if (err instanceof MemberApiError && (err.status === 403 || err.status === 404)) markOff()
      throw err
    }
  }, [hydrate, markOff])

  const applyProfile = useCallback((profile: MemberProfile) => {
    const c = currentRef.current
    if (c.status !== 'signedIn' || (c.profile !== null && c.profile.id !== profile.id)) return
    // A /me check of the current generation is running: the session is being
    // checked again (status 'checking'), and its answer will carry the profile.
    if (meGenRef.current !== 0 && meGenRef.current === genRef.current) return
    // Any older /me answer still on its way must not overwrite this profile.
    genRef.current++
    setState((s) => (s.status === 'signedIn' ? { ...s, profile } : s))
  }, [])

  const signOut = useCallback(() => {
    endedRef.current++
    genRef.current++
    clearMemberToken()
    setState({ key: keyRef.current ?? '', status: offRef.current ? 'off' : 'signedOut', account: null, profile: null })
  }, [])

  const value = useMemo<MemberAuthValue>(
    () => ({
      status: current.status,
      account: current.account,
      profile: current.profile,
      signIn,
      signUp,
      verifyEmail,
      resendVerification,
      signUpWithInvite,
      signUpWithTeamCode,
      signOut,
      refresh: hydrate,
      markOff,
      changePassword,
      applyProfile,
    }),
    [current.status, current.account, current.profile, signIn, signUp, verifyEmail, resendVerification, signUpWithInvite, signUpWithTeamCode, signOut, hydrate, markOff, changePassword, applyProfile],
  )

  return (
    <MemberAuthContext.Provider value={hydKey === null || current.status === 'off' ? MEMBER_AUTH_OFF : value}>
      {children}
    </MemberAuthContext.Provider>
  )
}
